import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import StudentLayout from '@/components/layouts/StudentLayout';
import { 
  Slice, ArrowLeft, Trophy, Star, Clock,
  RotateCcw, Zap, TrendingUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const FractionFrenzyGame = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  
  const [gameState, setGameState] = useState('ready');
  const [difficulty, setDifficulty] = useState('medium');
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [timeLeft, setTimeLeft] = useState(60);
  const [playerPosition, setPlayerPosition] = useState({ x: 50, y: 50 });
  const [collectibles, setCollectibles] = useState([]);
  const [targetFraction, setTargetFraction] = useState(null);
  const [collected, setCollected] = useState(0);
  const [streak, setStreak] = useState(0);
  const [powerUp, setPowerUp] = useState(null);

  // Difficulty settings
  const difficultySettings = {
    easy: { time: 90, speed: 3, wrongCount: 3, denominators: [2, 3, 4] },
    medium: { time: 60, speed: 5, wrongCount: 5, denominators: [2, 3, 4, 5, 6, 8] },
    hard: { time: 45, speed: 7, wrongCount: 7, denominators: [2, 3, 4, 5, 6, 8, 10, 12] }
  };

  // Generate target fraction
  const generateTarget = useCallback(() => {
    const settings = difficultySettings[difficulty];
    const denominators = settings.denominators;
    const denom = denominators[Math.floor(Math.random() * Math.min(level + 2, denominators.length))];
    const numer = Math.floor(Math.random() * (denom - 1)) + 1;
    return { numerator: numer, denominator: denom, display: `${numer}/${denom}` };
  }, [level, difficulty]);

  // Generate collectibles (fractions on screen)
  const spawnCollectibles = useCallback(() => {
    if (!targetFraction) return;
    
    const settings = difficultySettings[difficulty];
    const items = [];
    const correctCount = Math.min(2 + Math.floor(level / 2), 4);
    
    // Add correct equivalent fractions
    for (let i = 0; i < correctCount; i++) {
      const multiplier = i + 1;
      items.push({
        id: Date.now() + i,
        numerator: targetFraction.numerator * multiplier,
        denominator: targetFraction.denominator * multiplier,
        x: Math.random() * 80 + 10,
        y: Math.random() * 70 + 10,
        isCorrect: true,
        points: 20 + (multiplier * 5)
      });
    }
    
    // Add wrong fractions based on difficulty
    const wrongCount = settings.wrongCount + level;
    for (let i = 0; i < wrongCount; i++) {
      let numer, denom;
      do {
        denom = Math.floor(Math.random() * 10) + 2;
        numer = Math.floor(Math.random() * (denom - 1)) + 1;
      } while (numer / denom === targetFraction.numerator / targetFraction.denominator);
      
      items.push({
        id: Date.now() + correctCount + i,
        numerator: numer,
        denominator: denom,
        x: Math.random() * 80 + 10,
        y: Math.random() * 70 + 10,
        isCorrect: false,
        points: -10
      });
    }
    
    // Add power-up occasionally
    if (Math.random() > 0.7) {
      items.push({
        id: Date.now() + 100,
        isPowerUp: true,
        type: Math.random() > 0.5 ? 'time' : 'points',
        x: Math.random() * 80 + 10,
        y: Math.random() * 70 + 10
      });
    }
    
    setCollectibles(items);
  }, [targetFraction, level, difficulty]);

  // Start game
  const startGame = useCallback((selectedDifficulty) => {
    if (selectedDifficulty) setDifficulty(selectedDifficulty);
    const settings = difficultySettings[selectedDifficulty || difficulty];
    setGameState('playing');
    setScore(0);
    setLevel(1);
    setTimeLeft(settings.time);
    setCollected(0);
    setStreak(0);
    setPowerUp(null);
    setPlayerPosition({ x: 50, y: 50 });
    const target = generateTarget();
    setTargetFraction(target);
  }, [generateTarget, difficulty]);

  // Timer
  useEffect(() => {
    if (gameState === 'playing' && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(t => t - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && gameState === 'playing') {
      setGameState('finished');
    }
  }, [gameState, timeLeft]);

  // Spawn collectibles when target changes
  useEffect(() => {
    if (targetFraction && gameState === 'playing') {
      spawnCollectibles();
    }
  }, [targetFraction, gameState, spawnCollectibles]);

  // Level up
  useEffect(() => {
    if (collected > 0 && collected % 5 === 0) {
      setLevel(l => l + 1);
      const newTarget = generateTarget();
      setTargetFraction(newTarget);
      toast.success(`Level ${level + 1}! New target: ${newTarget.display}`);
    }
  }, [collected, generateTarget, level]);

  // Keyboard controls
  useEffect(() => {
    if (gameState !== 'playing') return;
    
    const settings = difficultySettings[difficulty];
    const handleKeyDown = (e) => {
      const speed = settings.speed;
      setPlayerPosition(prev => {
        let newPos = { ...prev };
        switch (e.key) {
          case 'ArrowUp': case 'w': case 'W':
            newPos.y = Math.max(5, prev.y - speed);
            break;
          case 'ArrowDown': case 's': case 'S':
            newPos.y = Math.min(85, prev.y + speed);
            break;
          case 'ArrowLeft': case 'a': case 'A':
            newPos.x = Math.max(5, prev.x - speed);
            break;
          case 'ArrowRight': case 'd': case 'D':
            newPos.x = Math.min(95, prev.x + speed);
            break;
        }
        return newPos;
      });
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [gameState, difficulty]);

  // Collision detection
  useEffect(() => {
    if (gameState !== 'playing') return;
    
    const checkCollisions = () => {
      collectibles.forEach(item => {
        const dist = Math.sqrt(
          Math.pow(playerPosition.x - item.x, 2) + 
          Math.pow(playerPosition.y - item.y, 2)
        );
        
        if (dist < 8) {
          // Collected!
          setCollectibles(prev => prev.filter(c => c.id !== item.id));
          
          if (item.isPowerUp) {
            if (item.type === 'time') {
              setTimeLeft(t => t + 10);
              toast.success('+10 seconds!');
            } else {
              setScore(s => s + 50);
              toast.success('+50 bonus points!');
            }
            setPowerUp(item.type);
            setTimeout(() => setPowerUp(null), 1000);
          } else if (item.isCorrect) {
            const bonus = streak * 5;
            setScore(s => s + item.points + bonus);
            setStreak(st => st + 1);
            setCollected(c => c + 1);
            toast.success(`+${item.points + bonus}! ${item.numerator}/${item.denominator} = ${targetFraction.display}`);
          } else {
            setScore(s => Math.max(0, s + item.points));
            setStreak(0);
            toast.error(`${item.numerator}/${item.denominator} ≠ ${targetFraction.display}`);
          }
        }
      });
    };
    
    const interval = setInterval(checkCollisions, 50);
    return () => clearInterval(interval);
  }, [gameState, playerPosition, collectibles, streak, targetFraction]);

  // Save game on finish
  useEffect(() => {
    if (gameState === 'finished') {
      const saveGame = async () => {
        try {
          await api.post('/games/complete', {
            game_id: 'fraction-frenzy',
            score: score,
            time_seconds: 60 - timeLeft,
            metadata: { level, fractions_collected: collected, best_streak: streak }
          });
        } catch (error) {
          console.error('Error saving game:', error);
        }
      };
      saveGame();
    }
  }, [gameState, score, level, collected, streak, timeLeft, api]);

  return (
    <StudentLayout>
      <div data-testid="fraction-frenzy-game" className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <button 
            onClick={() => navigate('/student/games')}
            className="flex items-center gap-2 text-slate-600 hover:text-purple-600 font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Games
          </button>
          
          {gameState === 'playing' && (
            <div className="flex items-center gap-4">
              <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${timeLeft <= 10 ? 'bg-red-100' : 'bg-emerald-100'}`}>
                <Clock className={`w-4 h-4 ${timeLeft <= 10 ? 'text-red-600' : 'text-emerald-600'}`} />
                <span className={`font-bold ${timeLeft <= 10 ? 'text-red-600' : 'text-emerald-700'}`}>{timeLeft}s</span>
              </div>
              <div className="flex items-center gap-2 bg-amber-100 px-3 py-1 rounded-full">
                <Star className="w-4 h-4 text-amber-600" />
                <span className="font-bold text-amber-700">{score}</span>
              </div>
            </div>
          )}
        </div>

        {/* Game Title */}
        <div className="student-card bg-gradient-to-r from-yellow-100 to-orange-100 border-orange-300 mb-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-orange-500 rounded-2xl border-2 border-gray-800 flex items-center justify-center">
              <Slice className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-slate-800">Fraction Frenzy</h1>
              <p className="text-slate-600">
                {gameState === 'playing' && targetFraction 
                  ? `Collect fractions equal to ${targetFraction.display}!` 
                  : 'Collect equivalent fractions!'}
              </p>
            </div>
          </div>
        </div>

        {/* Ready Screen */}
        {gameState === 'ready' && (
          <div className="student-card text-center py-12 bg-gradient-to-b from-orange-400 to-yellow-500">
            <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
              <Slice className="w-14 h-14 text-orange-500" />
            </div>
            <h2 className="font-heading text-3xl font-bold text-white mb-4">Fraction Frenzy</h2>
            <p className="text-lg text-orange-100 mb-6">
              Move around and collect fractions that are equivalent to the target!
            </p>
            
            {/* Difficulty Selection */}
            <div className="mb-8">
              <p className="text-orange-100 mb-4">Select Difficulty:</p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => setDifficulty('easy')}
                  className={`px-6 py-3 rounded-xl font-bold transition-all ${
                    difficulty === 'easy' 
                      ? 'bg-green-500 text-white scale-105' 
                      : 'bg-orange-600 text-orange-100 hover:bg-orange-500'
                  }`}
                >
                  Easy
                  <span className="block text-xs opacity-75">90s, simple fractions</span>
                </button>
                <button
                  onClick={() => setDifficulty('medium')}
                  className={`px-6 py-3 rounded-xl font-bold transition-all ${
                    difficulty === 'medium' 
                      ? 'bg-amber-600 text-white scale-105' 
                      : 'bg-orange-600 text-orange-100 hover:bg-orange-500'
                  }`}
                >
                  Medium
                  <span className="block text-xs opacity-75">60s, more distractors</span>
                </button>
                <button
                  onClick={() => setDifficulty('hard')}
                  className={`px-6 py-3 rounded-xl font-bold transition-all ${
                    difficulty === 'hard' 
                      ? 'bg-red-500 text-white scale-105' 
                      : 'bg-orange-600 text-orange-100 hover:bg-orange-500'
                  }`}
                >
                  Hard
                  <span className="block text-xs opacity-75">45s, complex fractions</span>
                </button>
              </div>
            </div>
            
            <div className="text-orange-100 mb-8 space-y-1">
              <p>⬆️⬇️⬅️➡️ or WASD to move</p>
              <p>🟢 Green = Correct fraction</p>
              <p>🔴 Red = Wrong fraction</p>
              <p>⭐ Collect power-ups for bonuses!</p>
            </div>
            <Button onClick={() => startGame(difficulty)} className="bg-white text-orange-600 hover:bg-orange-50 text-lg px-10 py-6">
              Start Collecting
              <Zap className="w-6 h-6 ml-2" />
            </Button>
          </div>
        )}

        {/* Playing Screen */}
        {gameState === 'playing' && targetFraction && (
          <div className="student-card bg-gradient-to-b from-yellow-400 to-orange-500 min-h-[400px] relative overflow-hidden">
            {/* Target Display */}
            <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-white px-6 py-2 rounded-full shadow-lg z-20">
              <span className="text-orange-600 font-bold">Target: </span>
              <span className="text-2xl font-heading font-bold text-slate-800">{targetFraction.display}</span>
            </div>
            
            {/* Streak Display */}
            {streak > 1 && (
              <div className="absolute top-4 right-4 bg-amber-500 px-3 py-1 rounded-full z-20">
                <span className="text-white font-bold">{streak}x Streak!</span>
              </div>
            )}
            
            {/* Power-up indicator */}
            {powerUp && (
              <div className="absolute top-4 left-4 bg-purple-500 px-3 py-1 rounded-full animate-pulse z-20">
                <span className="text-white font-bold">{powerUp === 'time' ? '⏰ +10s' : '💰 +50'}</span>
              </div>
            )}
            
            {/* Game Area */}
            <div className="relative h-80 mt-12">
              {/* Collectibles */}
              {collectibles.map(item => (
                <div
                  key={item.id}
                  className="absolute transform -translate-x-1/2 -translate-y-1/2 transition-all"
                  style={{ left: `${item.x}%`, top: `${item.y}%` }}
                >
                  {item.isPowerUp ? (
                    <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center animate-pulse shadow-lg shadow-purple-500/50">
                      <span className="text-white text-lg">{item.type === 'time' ? '⏰' : '💎'}</span>
                    </div>
                  ) : (
                    <div className={`w-14 h-14 rounded-lg flex items-center justify-center shadow-lg ${
                      item.isCorrect 
                        ? 'bg-emerald-500 border-2 border-emerald-300 shadow-emerald-500/50' 
                        : 'bg-red-500 border-2 border-red-300 shadow-red-500/50'
                    }`}>
                      <span className="text-white font-bold text-sm">{item.numerator}/{item.denominator}</span>
                    </div>
                  )}
                </div>
              ))}
              
              {/* Player */}
              <div
                className="absolute transform -translate-x-1/2 -translate-y-1/2 transition-all duration-75 z-10"
                style={{ left: `${playerPosition.x}%`, top: `${playerPosition.y}%` }}
              >
                <div className="w-12 h-12 bg-white rounded-full border-4 border-orange-600 flex items-center justify-center shadow-lg">
                  <span className="text-2xl">😋</span>
                </div>
              </div>
            </div>
            
            {/* Controls hint */}
            <div className="text-center text-orange-100 text-sm hidden md:block">
              Use arrow keys or WASD to move
            </div>
            
            {/* Mobile Touch Controls */}
            <div className="md:hidden grid grid-cols-3 gap-2 mt-4 max-w-[200px] mx-auto">
              <div></div>
              <button 
                onTouchStart={() => setPlayerPosition(p => ({...p, y: Math.max(5, p.y - 8)}))}
                className="w-14 h-14 bg-white/30 rounded-xl flex items-center justify-center active:bg-white/50"
              >
                <span className="text-2xl">⬆️</span>
              </button>
              <div></div>
              <button 
                onTouchStart={() => setPlayerPosition(p => ({...p, x: Math.max(5, p.x - 8)}))}
                className="w-14 h-14 bg-white/30 rounded-xl flex items-center justify-center active:bg-white/50"
              >
                <span className="text-2xl">⬅️</span>
              </button>
              <div className="w-14 h-14"></div>
              <button 
                onTouchStart={() => setPlayerPosition(p => ({...p, x: Math.min(95, p.x + 8)}))}
                className="w-14 h-14 bg-white/30 rounded-xl flex items-center justify-center active:bg-white/50"
              >
                <span className="text-2xl">➡️</span>
              </button>
              <div></div>
              <button 
                onTouchStart={() => setPlayerPosition(p => ({...p, y: Math.min(85, p.y + 8)}))}
                className="w-14 h-14 bg-white/30 rounded-xl flex items-center justify-center active:bg-white/50"
              >
                <span className="text-2xl">⬇️</span>
              </button>
              <div></div>
            </div>
          </div>
        )}

        {/* Finished Screen */}
        {gameState === 'finished' && (
          <div className="student-card text-center py-12 bg-gradient-to-b from-orange-400 to-yellow-500">
            <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce shadow-lg">
              <Trophy className="w-12 h-12 text-amber-500" />
            </div>
            <h2 className="font-heading text-3xl font-bold text-white mb-2">Time's Up!</h2>
            <p className="text-xl text-orange-100 mb-4">
              Level {level} reached!
            </p>
            
            <div className="grid grid-cols-2 gap-4 my-8 max-w-xs mx-auto">
              <div className="bg-white/20 rounded-xl p-4">
                <p className="text-3xl font-bold text-white">{collected}</p>
                <p className="text-sm text-orange-100">Collected</p>
              </div>
              <div className="bg-white/20 rounded-xl p-4">
                <p className="text-3xl font-bold text-white">{streak}</p>
                <p className="text-sm text-orange-100">Best Streak</p>
              </div>
            </div>
            
            <div className="flex items-center justify-center gap-2 mb-8">
              <Star className="w-8 h-8 text-white fill-white" />
              <span className="font-heading text-4xl font-bold text-white">+{score}</span>
              <span className="text-orange-100 text-xl">points</span>
            </div>
            
            <div className="flex gap-4 justify-center">
              <Button variant="outline" onClick={() => startGame()} className="border-white text-white hover:bg-white/20">
                <RotateCcw className="w-4 h-4 mr-2" />
                Play Again
              </Button>
              <Button onClick={() => navigate('/student/games')} className="bg-white text-orange-600 hover:bg-orange-50">
                More Games
              </Button>
            </div>
          </div>
        )}
      </div>
    </StudentLayout>
  );
};

export default FractionFrenzyGame;
